import React, { useContext, useEffect, useRef, useState } from 'react';
import './styles.scss';
import { useHistory, useLocation } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { generatePDF } from '../../Portfolio_R/SummaryCharts/utils/generatePdf';
import { getOnePagerReport } from '../../../api/projectApi';
import { LoadingComponent } from '../../../pages';
import { MappedFilters } from '../../Portfolio_R/SummaryCharts/components/MappedFilters';
import { normalizeData } from '../../Portfolio_R/SummaryCharts/utils/mapper_one_pager_data';
import { ReactComponent as IconBack } from '../../../assets/icons/u_arrow-left.svg';
import { ReactComponent as IconOnePager } from '../../../assets/icons/one-pager.svg';
import { ReactComponent as IconExtendedPager } from '../../../assets/icons/extended-icon.svg';
import { ReactComponent as IconCustomize } from '../../../assets/icons/u_edit-alt.svg';
import { ReactComponent as IconDownload } from '../../../assets/icons/u_download-alt.svg';

import Header from '../../Header/Header';
import usePortfolioName from '../../../hooks/usePortfolioName';
import { TagFilterContext } from '../../../context/TagFilterContext';
import { downloadOnePagerReport } from '../../Projects/ProjectsReport/utils/Downloaders/OnePagerReportDownload';
import AutoDismissAlert from '../../Projects/ProjectsReport/view/Common/AlertTooltips';
import { ERROR_IN_GENERATE_REPORT, SUCESSFUL_GENERATED_PROJECT_REPORT } from '../../Projects/ProjectsReport/utils/Constants/AlertStatusConstants';
import BaseModal from '../../Util/Modals/BaseModal';
import { styles } from '../../Util/Modals/Styles';
import SpinningLoader from '../../ProjectDetail/ProjectTabs/Loader/SpinningLoader';
import { getLabelDownloadingReport } from '../../Projects/ProjectsReport/utils/Validators/ReportValidations';
import { setPayload } from '../../../redux/actions/indicatorPayload';

const INDICATOR_PAYLOAD_SESSION_KEY = 'ONE_PAGER_INDICATOR_PAYLOAD';
const PROJECT_CODE_SESSION_KEY = 'ONE_PAGER_PROJECT_CODE';
const PORTFOLIO_NAME_SESSION_KEY = 'ONE_PAGER_PORTFOLIO_NAME';
const MAPPED_FILTERS_SESSION_KEY = 'ONE_PAGER_MAPPED_FILTERS';
const canUseSessionStorage = () => typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

export const OnePagerReportView = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const indicatorPayload = useSelector<any, any>(state => state.indicatorPayload.payload);
  const { labels } = useContext(TagFilterContext);
  const sectorsFromRedux = useSelector<any, any>(state => state.sectors);
  const divisionsFromRedux = useSelector<any, any>(state => state.divisions);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const from = queryParams.get('from');
  const projectId = queryParams.get('id');
  const projectCode = queryParams.get('code');
  const isPMRType = (queryParams.get('isPMRType') || '').toLowerCase() === "true";
  const portfolioName = usePortfolioName();
  const [selectedReport, setSelectedReport] = useState<string | null>('onePager');
  const [chartData, setChartData] = useState<any>(null);
  const cardContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasHydrated, setHasHydrated] = useState(false);
  const [enableDownload, setEnableDownload] = useState(true);
  const [enableCustomize, setEnableCustomize] = useState(false);
  const [alertConfig, setAlertConfig] = useState<any>();
  const [storedLabels, setStoredLabels] = useState<string[]>(() => {
    if (!canUseSessionStorage()) return [];
    try {
      const cached = sessionStorage.getItem(MAPPED_FILTERS_SESSION_KEY);
      return cached ? JSON.parse(cached) : [];
    } catch (error) {
      sessionStorage.removeItem(MAPPED_FILTERS_SESSION_KEY);
      return [];
    }
  });
  const [storedProjectCode, setStoredProjectCode] = useState<string | null>(projectCode);
  const [storedPortfolioName, setStoredPortfolioName] = useState<string | null>(portfolioName || null);

  useEffect(() => {
    if (!labels || !labels.length || !canUseSessionStorage()) return;
    setStoredLabels(labels);
    try {
      sessionStorage.setItem(MAPPED_FILTERS_SESSION_KEY, JSON.stringify(labels));
    } catch (error) {
      sessionStorage.removeItem(MAPPED_FILTERS_SESSION_KEY);
    }
  }, [labels]);

  useEffect(() => {
    if (projectCode) {
      setStoredProjectCode(projectCode);
    }
  }, [projectCode]);

  useEffect(() => {
    if (storedProjectCode || !canUseSessionStorage()) return;

    const cachedProjectCode = sessionStorage.getItem(PROJECT_CODE_SESSION_KEY);
    if (cachedProjectCode) {
      setStoredProjectCode(cachedProjectCode);
    }
  }, [storedProjectCode]);

  useEffect(() => {
    if (!storedProjectCode || !canUseSessionStorage()) return;

    try {
      sessionStorage.setItem(PROJECT_CODE_SESSION_KEY, storedProjectCode);
    } catch (error) {
      sessionStorage.removeItem(PROJECT_CODE_SESSION_KEY);
    }
  }, [storedProjectCode]);

  useEffect(() => {
    if (portfolioName) {
      setStoredPortfolioName(portfolioName);
    }
  }, [portfolioName]);

  useEffect(() => {
    if (storedPortfolioName || !canUseSessionStorage()) return;

    const cachedPortfolioName = sessionStorage.getItem(PORTFOLIO_NAME_SESSION_KEY);
    if (cachedPortfolioName) {
      setStoredPortfolioName(cachedPortfolioName);
    }
  }, [storedPortfolioName]);

  useEffect(() => {
    if (!storedPortfolioName || !canUseSessionStorage()) return;

    try {
      sessionStorage.setItem(PORTFOLIO_NAME_SESSION_KEY, storedPortfolioName);
    } catch (error) {
      sessionStorage.removeItem(PORTFOLIO_NAME_SESSION_KEY);
    }
  }, [storedPortfolioName]);

  const projectCodeToUse = storedProjectCode || projectCode || '';
  const portfolioNameToUse = storedPortfolioName || portfolioName || '';

  useEffect(() => {
    if (indicatorPayload || !canUseSessionStorage()) return;

    const cachedPayload = sessionStorage.getItem(INDICATOR_PAYLOAD_SESSION_KEY);
    if (!cachedPayload) return;

    try {
      const parsedPayload = JSON.parse(cachedPayload);
      dispatch(setPayload(parsedPayload));
    } catch (error) {
      sessionStorage.removeItem(INDICATOR_PAYLOAD_SESSION_KEY);
    }
  }, [indicatorPayload, dispatch]);

  useEffect(() => {
    if (!indicatorPayload || !canUseSessionStorage()) return;

    try {
      sessionStorage.setItem(INDICATOR_PAYLOAD_SESSION_KEY, JSON.stringify(indicatorPayload));
    } catch (error) {
      sessionStorage.removeItem(INDICATOR_PAYLOAD_SESSION_KEY);
    }
  }, [indicatorPayload]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setHasHydrated(true);
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  const handleOnClick = async () => {
    if (!indicatorPayload) return;
    const data = await getOnePagerReport(indicatorPayload);
    const dataToNormalize = normalizeData(data);
    setChartData(dataToNormalize);
  };

  useEffect(() => {
    handleOnClick();
  }, [indicatorPayload]);

  useEffect(() => {
    if (alertConfig === null && from === 'project') {
      handleBackClick();
    }
  }, [alertConfig])

  const downloadReport = async () => {
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
      return;
    }

    setIsLoading(true);

    const reportFn = from === "project"
      ? () => downloadOnePagerReport(projectId, setIsLoading)
      : () => handlePortfolioDownload();

    await handleReportDownload(reportFn);
  };

  const ensureChartData = async () => {
    if (chartData) return chartData;

    if (!indicatorPayload) {
      throw new Error('There are no parameters to generate the report.');
    }

    const data = await getOnePagerReport(indicatorPayload);
    const normalized = normalizeData(data);
    setChartData(normalized);
    return normalized;
  };

  const handlePortfolioDownload = async () => {
    setIsLoading(true);

    try {
      const ensured = await ensureChartData();

      const filters = indicatorPayload?.Filters || [];
      const sectorFilter = filters.find(f => f.field === 'SectorId');
      const divisionFilter = filters.find(f => f.field === 'DivisionId');

      const sectorLabels = sectorFilter && sectorFilter.value && Array.isArray(sectorFilter.value)
        ? sectorFilter.value
          .map((id: number) => sectorsFromRedux.find((s: any) => s.id === id))
          .filter(Boolean)
          .map((s: any) => `${s.code} - ${s.description}`)
          .join(', ')
        : null;

      const divisionLabels = divisionFilter && divisionFilter.value && Array.isArray(divisionFilter.value)
        ? divisionFilter.value
          .map((id: number) => divisionsFromRedux.find((d: any) => d.id === id))
          .filter(Boolean)
          .map((d: any) => `${d.code} - ${d.name}`)
          .join(', ')
        : null;

      const filtersAppliedForPdf = [null, sectorLabels, divisionLabels];

      await generatePDF({
        loan: ensured.loan,
        tcp: ensured.tcp,
        igr: ensured.igr,
        generalData: ensured.generalData,
        filtersApplied: filtersAppliedForPdf,
        onLoading: setIsLoading,
        portfolioName: portfolioNameToUse,
      });
      setAlertConfig(SUCESSFUL_GENERATED_PROJECT_REPORT);
    } catch (error) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
    }
    setIsLoading(false);
  };

  const handleReportDownload = async (downloadFn) => {
    try {
      if (typeof navigator !== 'undefined' && !navigator.onLine) {
        throw new Error('offline');
      }

      await downloadFn();
    } catch (err) {
      setAlertConfig(ERROR_IN_GENERATE_REPORT);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomizeExtendeReport = async () => {
    let basePath = '/extendedreport';
    if (from === 'project') {
      basePath += `?from=${from}&id=${projectId}&code=${projectCodeToUse}&isPMRType=${isPMRType}`;
    }

    history.push(basePath);
  }

  const handleBackClick = () => {
    sessionStorage.removeItem(MAPPED_FILTERS_SESSION_KEY);
    let basePath = 'home';

    if (from === 'project') {
      basePath = `projectdetail?id=${projectId}`;
    }

    history.push(basePath);
  }

  if (!hasHydrated) {
    return <LoadingComponent />;
  }

  return (
    <div className="c-report">
      <Header detailProject />

      <div className="c-report-header">
        <button onClick={() => handleBackClick()} className="btn btn-primary-text back-btn">
          <IconBack />
          {t('memory_help.button_back')}
        </button>
        <div className="c-header-content">
          <h4 className="header-title">
            {from === 'project'
              ? `${t('one_pager_report.new_create_project_report')} ${projectCodeToUse}`
              : `${t('one_pager_report.create_portfolio_report')} ${portfolioNameToUse}`}
          </h4>
          <div className="header-filter-details">
            <MappedFilters data={labels && labels.length ? labels : storedLabels} />
          </div>
        </div>
      </div>
      <div className="c-card-content" onClick={() => {
        setEnableDownload(false);
        setEnableCustomize(false);
      }}>
        <p className='c-card-content-title'> {t('one_pager_report.new_select_type_report')}</p>
        <div
          className={`report-card ${selectedReport === 'onePager' ? 'selected' : ''}`}
          tabIndex={0}
          ref={cardContainerRef}
          onClick={($event) => {
            setEnableDownload(true);
            setEnableCustomize(false);
            $event.stopPropagation();
          }}
        >
          <h3 className="report-card-title">
            <IconOnePager />{' '}
            {from === 'project' ? `${t('one_pager_report.new_one_page_report')}` : `${t('one_pager_report.one_pager_report_main')}`}
          </h3>
          <p>
            {from === 'project'
              ? `${t('one_pager_report.new_overview_single_page')}`
              : `${t('one_pager_report.one_pager_content')}`}
          </p>
        </div>
        <div
          className={`report-card ${selectedReport === 'extendedReport' ? 'selected' : ''}
                }`}
          tabIndex={0}
          ref={cardContainerRef}
          onClick={($event) => {
            setEnableDownload(false);
            setEnableCustomize(true);
            $event.stopPropagation();
          }}
          onFocus={() => setSelectedReport('extededReport')}
        >
          <h3 className="report-card-title">
            <IconExtendedPager />
            {t('one_pager_report.extended_report')}
          </h3>

          <p>
            {from === 'project'
              ? `${t('one_pager_report.extended_project_report_details')}`
              : `${t('one_pager_report.extended_report_details')}`}
          </p>
        </div>
      </div>

      <div className='c-btns'>
        {enableCustomize && (
          <button
            className='btn btn-primary-outlined margin-btn'
            onClick={() => handleCustomizeExtendeReport()}>
            <IconCustomize />
            {t('extended_report.customize')}</button>
        )}
        {enableDownload &&
          (<button
            className='btn btn-primary'
            onClick={() => downloadReport()}
          >
            <IconDownload />
            {t('extended_report.download_pdf')}
          </button>)}
      </div>
      <BaseModal
        isOpen={isLoading}
        style={styles}
        className='downloading-report'
      >
        <SpinningLoader customStyle={{ marginTop: 0 }} customText={getLabelDownloadingReport(from)} />
      </BaseModal>
      {alertConfig && (
        <AutoDismissAlert
          message={alertConfig.message}
          title={alertConfig.title}
          severity={alertConfig.severity}
          duration={alertConfig.duration}
          onClose={() => setAlertConfig(null)}
        />
      )}
    </div>
  );
};
